<template>
  <div class="form-canvas">
    <!-- Sidebar -->
  <div class="sidebar">
  <div v-if="[...fixedFields].length" class="section">
  <h3>Fixed Fields</h3>
  <div class="line"></div>
  <div class="fixed-fields-container">
    <div
      v-for="(field, index) in fixedFields"
      :key="field.id || index"
      class="fixed-field-item pointer"
      :class="{ 'dragging': draggedFixedIndex === index }"
      @dragover.prevent
      @dragenter.prevent
      @drop="handleFixedDrop(index, $event)"
    >
      <!-- Drag Handle for Fixed Fields -->
      <div
        class="drag-handle"
        draggable="true"
        @dragstart="handleFixedDragStart(index, $event)"
        @dragend="handleFixedDragEnd"
      >
        <img
          src="~@/assets/images/icons/drag-handle.svg"
          alt="drag handle"
          class="drag-icon"
          style="width: 12px; height: 12px; opacity: 0.5; cursor: grab;"
        />
      </div>

      <div class="field-left">
        <img
          v-if="typeNameById[field.field_type_id] && iconMap[typeNameById[field.field_type_id]]"
          :src="iconMap[typeNameById[field.field_type_id]]"
          alt="field icon"
          class="field-icon"
        />
        <span v-overflow-tooltip class="field-name">{{ field.caption }}</span>
      </div>
    </div>
  </div>
</div>

<div class="section custom-fields-section">
  <h3>Custom Fields</h3>
    <div v-if="!fixedFields.length" class="line"></div>
  <div class="custom-fields-scroll-wrapper">
    <div class="custom-fields-container">
      <div
        v-for="(field, index) in customFields"
        :key="field.id || index"
        class="custom-field-item pointer"
        :class="{ 'dragging': draggedCustomIndex === index }"
        @dragover.prevent
        @dragenter.prevent
        @drop="handleCustomDrop(index, $event)"
      >
        <!-- Drag Handle for Custom Fields -->
        <div
          class="drag-handle"
          draggable="true"
          @dragstart="handleCustomDragStart(index, $event)"
          @dragend="handleCustomDragEnd"
        >
          <img
            src="~@/assets/images/icons/drag-handle.svg"
            alt="drag handle"
            class="drag-icon"
            style="width: 12px; height: 12px; opacity: 0.5; cursor: grab;"
          />
        </div>

        <div class="field-left" @click="enableEdit(index, getFieldName(field))">
          <img
           :src="iconMap[getFieldKey(field)]"
            alt=""
            class="field-icon"
          />
          <span v-overflow-tooltip v-if="editingFieldIndex !== index" class="field-name pointer">
            {{ getFieldName(field) }}
          </span>
          <input
            v-else
            v-model="editingFieldName"
            @keyup.enter="saveFieldName(index)"
            @blur="saveFieldName(index)"
            class="edit-input"
          />
        </div>
        <img
          v-if="editingFieldIndex === null"
          src="~@/assets/images/trash-2.svg"
          class="delete-icon"
          @click="deleteCustomField(index)"
        />
      </div>
    </div>
  </div>
  <button v-if="editingFieldIndex === null" class="add-field-btn" @click="showFieldModal = true">+ Add Field</button>
</div>
      <FormTemplateModal
        :customFields="customFields"
        :fields="customFormFields"
        :customLists="customLists"
        :visible="showFieldModal"
        :formType="formData.formType"
        @add-field="handleAddCustomField"
        @close="showFieldModal = false"
      />
    </div>

<!-- Right Preview -->
 <div class="preview-wrapper">
   <div class="preview">
     <h2 v-if="!preview" class="template-name center">{{ formData.templateName }}</h2>
     <label v-if="!preview" class="form-type-name center" for="">{{ formData.formTypeName }}</label>
<div v-if="!preview && autogeneratedFields.length" class="form-info center">
  <span v-for="(field, index) in autogeneratedFields" :key="field.field_id" class="flex v-center">
    <span class="info-item">
      {{ field.caption }} : --
    </span>
    <span
      v-if="index < autogeneratedFields.length - 1"
      class="info-separator"
      style="margin: 0 8px;"
    >
      •
    </span>
  </span>
</div>

     <div class="preview-scroll-container">
       <div class="preview-fields-wrapper">
            <div v-if="[...fixedFields, ...customFields].length === 0" class="no-fields-message">
      Add a Custom Field
    </div>
       <div
         v-for="(field, index) in [...fixedFields, ...customFields]"
         :key="getFieldRenderKey(field)"
         class="preview-field"
       >
         <div class="field-header">
           <div style="display: flex; align-items: center; gap: 8px;">
             <span class="field-index">{{ (index + 1).toString().padStart(2, '0') }}</span>
             <span class="field-title">{{ field.caption }}</span>
             <span v-if="field.required" class="required-asterisk">*</span>
             <div v-if="getFieldKey(field) === 'CONFIGURATION_LIST' && getFieldValue(field)" class="config-list-name-pill">{{getFieldValue(field)}}</div>
           </div>

            <div v-if="getFieldKey(field) === 'CONFIGURATION_LIST'" class="abbreviation-toggle">
              <input v-tooltip="'Shown only when abbreviations are available'" :id="`use-abbreviation-${index}`"
                type="checkbox" v-model="field.use_abbreviation" />
              <label :for="`use-abbreviation-${index}`">Use Abbreviations</label>
            </div>
          </div>

          <div class="field-options">
            <div v-if="getFieldKey(field) === 'CONFIGURATION_LIST'" class="options-button"
              @click="openConfigurationListSelector(field, index)">
              <img src="~@/assets/images/settings.svg" alt="Settings" class="settings-icon" />
              <span class="options-label">Options</span>
            </div>
          </div>

         <div class="field-input">
           <input
           v-if="getFieldKey(field) !== 'TABLE'"
             class="placeholder-input"
             :placeholder="`Select (Placeholder)`"
             :value="getFieldValue(field)" disabled
           />
          <div v-show="getFieldKey(field) === 'TABLE'" class="field-inner">
            <TableTemplate
              :formTemplate="true"
              :fieldId="field.field_id"
              :data="field"
              :value="field.table_data"
              :formData="formData"
              :previewMode="preview"
              mode="TEMPLATE"
              @customChange="handleTable"
              ref="tableTemplateRef"
            />
          </div>
        </div>
           <div class="field-controls">
              <div class="toggle-wrapper">
                <label class="switch" :class="{ 'disabled': shouldDisableVisibilityToggle(field, index) }">
                 <input
                   type="checkbox"
                   v-model="field.visibility"
                   :disabled="shouldDisableVisibilityToggle(field, index) || field.form_field?.key === 'TABLE' || field?.key === 'TABLE'"
                   @change="handleVisibilityChange(field, index)"
                 />
                 <span class="slider round"></span>
               </label>
               <span class="toggle-label">Visibility</span>
             </div>
              <div class="toggle-wrapper">
                <label class="switch" :class="{ 'disabled': isFixedField(field)}">
                 <input
                   type="checkbox"
                   v-model="field.required"
                   :disabled="isFixedField(field) || field.form_field?.key === 'TABLE' || field?.key === 'TABLE'"
                 />
                 <span class="slider round"></span>
               </label>
               <span class="toggle-label">Required</span>
             </div>
           </div>
       </div>
       </div>
     </div>
   <div class="center">
     <button @click="saveTemplate" class="btn btn-black" :disabled="[...fixedFields, ...customFields].length === 0">
       <img src="~@/assets/images/icons/save.svg" alt="Save" class="button-icon pointer" />
       Save Template
     </button>
   </div>
   </div>
 </div>

    <modal
    :title="'Choose Custom List'"
    :open="openConfig"
    @close="openConfig = false"
    >

<div class="dropdown-container">
  <!-- Dropdown -->
  <div class="field-group">
    <label for="customListSelect">Select Custom List :</label>
    <select
      id="customListSelect"
      class="custom-select"
      v-model="selectedList"
    >
      <option disabled value="">-- Select a list --</option>
      <option
        v-for="list in customLists"
        :key="list.id"
        :value="list"
      >
        {{ list.name }}
      </option>
    </select>
  </div>

  <!-- Buttons -->
  <div class="actions">
    <button class="btn-2 btn-2-cancel" @click="cancelConfig">Cancel</button>
    <button
      class="btn-2 btn-2-confirm"
      :class="{ 'btn-2-confirm-active': selectedList }"
      @click="confirmConfig"
    >
      Confirm
    </button>
  </div>
</div>

    </modal>
  </div>
</template>

<script>
import { getFormFields, GetActiveCustomLists } from '@/api'
import config from '@/config'
import modal from '../common/modal.vue'
import FormTemplateModal from '@/components/form/formTemplateModal.vue'
import { alert } from '@/plugins/notification'
import { iconMap } from '@/utils/formFieldIcons'
import TableTemplate from '@/views/SETTINGS/tableTemplate.vue'

export default {
  name: 'FormCanvas',
  components: { FormTemplateModal, modal, TableTemplate },
  props: {
    formData: {
      type: Object,
      required: true
    },
    fixedFields: {
      type: Array,
      required: true
    },
    autogeneratedFields: {
      type: Array,
      default: () => [],
      required: true
    },
    customFields: {
      type: Array,
      required: true
    },
    preview: {
      type: Boolean
    }
  },
  data () {
    return {
      editingFieldIndex: null,
      editingFieldName: '',
      types: config.FORM_TYPE,
      selectedList: null,
      iconMap,
      selected: null,
      openConfig: false,
      customLists: [],
      customFormFields: [],
      showFieldModal: false,
      // Drag and drop state for custom fields
      draggedCustomIndex: null,
      draggedCustomField: null,
      // Drag and drop state for fixed fields
      draggedFixedIndex: null,
      draggedFixedField: null
    }
  },
  computed: {
    typeNameById () {
      return Object.fromEntries(
        Object.entries(this.types).map(([key, value]) => [value, key])
      )
    },
    allFields () {
      return [...this.fixedFields, ...this.customFields]
    },
    visibleFieldsCount () {
      return this.allFields.filter(field => field.visibility).length
    }
  },
  mounted () {
    GetActiveCustomLists().then(res => {
      this.customLists = res.core_custom_list.map(list => {
        return {
          id: list.id,
          name: list.name
        }
      })
    })
    getFormFields().then(res => {
      if (Array.isArray(res.core_form_fields)) {
        let filteredFields = res.core_form_fields.filter(field => field.enabled === true)
        // Filter out Material field if form type is JOB order (formType === 5)
        if (this.formData.formType === config.JOB_ORDER_FORM.form_type) {
          filteredFields = filteredFields.filter(field =>
            field.key.toLowerCase() !== 'materials'
          )
        }
        if (this.formData.formType === config.STANDARD_BOM_FORM.form_type || this.formData.formType === config.STANDARD_MATERIAL_FORM.form_type) {
          const allowedKeys = ['text', 'number', 'date', 'boolean', 'long_text', 'configuration_list', 'time', 'table']
          filteredFields = filteredFields.filter(field =>
            allowedKeys.includes(field.key.toLowerCase())
          )
        }
        this.customFormFields = filteredFields.map(field => ({
          caption: field.caption,
          id: field.id,
          key: field.key
        }))
      } else {
        this.customFormFields = []
      }
    })
  },
  methods: {
    // Check if a field is a fixed field
    isFixedField (field) {
      return this.fixedFields.includes(field)
    },

    // Check if visibility toggle should be disabled
    shouldDisableVisibilityToggle (field, index) {
      // If this field already has visibility true, don't disable it
      if (field.visibility) {
        return false
      }
      // If we already have 2 fields with visibility true, disable this toggle
      return this.visibleFieldsCount >= 2
    },

    // Handle visibility change
    handleVisibilityChange (field, index) {
      // This method can be used for additional logic if needed
      // The v-model will handle the basic toggle functionality
    },

    getFieldValue (field) {
      if (typeof field === 'string') {
        const foundField = this.autogeneratedFields.find(f => f.caption === field)
        return foundField?.field_name || null
      }
      if (this.getFieldKey(field) === 'CONFIGURATION_LIST') {
        // If custom_list_name exists, use it
        if (field.custom_list_name) {
          return field.custom_list_name
        }
        // If only custom_list_id exists, find the name from customLists
        if (field.custom_list_id && this.customLists.length) {
          const matchedList = this.customLists.find(list => list.id === field.custom_list_id)
          return matchedList ? matchedList.name : ''
        }
      }
      return ''
    },
    getFieldKey (field) {
    // For editing: key is inside form_field object
      if (field.form_field && field.form_field.key) {
        return field.form_field.key
      }
      // For creation: key is directly on field object
      return field.key
    },
    getFieldName (field) {
    // Priority: name > caption
      return field.caption || field.field_name
    },
    getFieldIdentity (field) {
      return field?.field_id || field?.id || field?.form_field_id || null
    },
    getFieldRenderKey (field) {
      return this.getFieldIdentity(field) || `${this.getFieldKey(field)}-${field.caption || field.field_name || 'field'}`
    },
    findFieldByIdentity (fieldId) {
      return this.allFields.find(field => String(this.getFieldIdentity(field)) === String(fieldId)) || null
    },
    // Custom Fields drag and drop methods
    handleCustomDragStart (index, event) {
      this.draggedCustomIndex = index
      this.draggedCustomField = this.customFields[index]
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target.outerHTML)

      // Add visual feedback
      setTimeout(() => {
        event.target.style.opacity = '0.5'
      }, 0)
    },

    handleCustomDragEnd (event) {
      event.target.style.opacity = '1'
      this.draggedCustomIndex = null
      this.draggedCustomField = null
    },

    handleCustomDrop (dropIndex, event) {
      event.preventDefault()

      if (this.draggedCustomIndex === null || this.draggedCustomIndex === dropIndex) {
        return
      }

      // Create a copy of customFields array
      const newCustomFields = [...this.customFields]

      // Remove the dragged field from its original position
      const draggedField = newCustomFields.splice(this.draggedCustomIndex, 1)[0]

      // Insert the dragged field at the new position
      newCustomFields.splice(dropIndex, 0, draggedField)

      // Update the customFields array
      this.customFields.splice(0, this.customFields.length, ...newCustomFields)
    },

    // Fixed Fields drag and drop methods
    handleFixedDragStart (index, event) {
      this.draggedFixedIndex = index
      this.draggedFixedField = this.fixedFields[index]
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target.outerHTML)

      // Add visual feedback
      setTimeout(() => {
        event.target.style.opacity = '0.5'
      }, 0)
    },

    handleFixedDragEnd (event) {
      event.target.style.opacity = '1'
      this.draggedFixedIndex = null
      this.draggedFixedField = null
    },

    handleFixedDrop (dropIndex, event) {
      event.preventDefault()

      if (this.draggedFixedIndex === null || this.draggedFixedIndex === dropIndex) {
        return
      }

      // Emit event to parent to handle fixed fields reordering
      // since fixedFields is a prop, we need the parent to handle the update
      this.$emit('reorder-fixed-fields', {
        fromIndex: this.draggedFixedIndex,
        toIndex: dropIndex
      })
    },
    handleTable (transformedTable) {
      const targetField = this.findFieldByIdentity(transformedTable.field_id)
      if (targetField) {
        this.$set(targetField, 'table_data', transformedTable.value)
      }
    },
    isConfigurationListCellFilled (cell) {
      return Array.isArray(cell) && cell.length > 0
    },
    validateConfigurationListTableColumns (tableData, fieldLabel) {
      const columns = tableData?.columns || []
      const rows = tableData?.rows || []

      for (const column of columns) {
        if (column?.type !== 'CONFIGURATION_LIST') continue

        let filledCount = 0
        let emptyCount = 0

        rows.forEach(row => {
          const cell = row?.[column.id]
          if (this.isConfigurationListCellFilled(cell)) {
            filledCount++
          } else {
            emptyCount++
          }
        })

        if (filledCount > 0 && emptyCount > 0) {
          alert(`"${fieldLabel}" -> "${column.label || column.id}": Either fill all rows or leave all rows empty.`)
          return false
        }
      }

      return true
    },
    async validateTableRowColumns (fieldId) {
      const tableRefs = this.$refs.tableTemplateRef
      if (!tableRefs || !tableRefs.length) return false
      const tableRef = tableRefs.find(ref => String(ref.fieldId) === String(fieldId))
      if (!tableRef) return false

      const isValid = await tableRef.saveFormTemplate()
      if (!isValid) return false

      const serializedTableData = tableRef.componentValue?.update || tableRef.componentValue
      const targetField = this.findFieldByIdentity(fieldId)
      if (targetField && serializedTableData) {
        this.$set(targetField, 'table_data', serializedTableData)
      }
      if (!this.validateConfigurationListTableColumns(serializedTableData, tableRef.data?.caption || 'Table')) {
        return false
      }

      return true
    },
    async saveTemplate () {
      const allFields = [...this.fixedFields, ...this.autogeneratedFields, ...this.customFields]
      const configListFields = allFields.filter(field => this.getFieldKey(field) === 'CONFIGURATION_LIST')
      const unselectedConfigFields = configListFields.filter(field => !field.custom_list_id)
      const tableFields = allFields.filter(field => (field?.form_field?.key === 'TABLE' || field?.key === 'TABLE') && field.table_data !== null)
      for (const tableField of tableFields) {
        const isTableValid = await this.validateTableRowColumns(tableField.field_id)
        if (!isTableValid) {
          return
        }
      }
      if (unselectedConfigFields.length > 0) {
        alert('Please select a custom list for all Configuration List fields before saving')
        return
      }
      const transformedFields = allFields.map(field => {
        let saveAsFixed = false
        if (field.autogenerated) {
          saveAsFixed = true
        } else {
          saveAsFixed = this.fixedFields.some(f =>
            (f.field_id || f.id) === (field.field_id || field.id)
          )
        }
        return {
          autogenerated: !!field.autogenerated,
          caption: field.caption,
          custom_list_id: field.custom_list_id ? field.custom_list_id : undefined,
          field_name: (field.name || field.caption).replace(/\s+/g, '_').toLowerCase(),
          field_type_id: this.getFieldTypeId(field),
          fixed: saveAsFixed,
          required: !!field.required,
          visibility: !!field.visibility,
          field_id: field.field_id,
          use_abbreviation: !!field.use_abbreviation || false,
          ...((field?.key === 'TABLE' || field?.form_field?.key === 'TABLE') && { table_data: field.table_data })
        }
      })
      const visibleFieldsCount = transformedFields.filter(f => f.visibility).length

      if (visibleFieldsCount < 2) {
        alert('At least 2 fields should have visibility')
        return
      } else if (visibleFieldsCount > 2) {
        alert('Only 2 fields should have visibility, not more')
        return
      }
      this.$emit('save-template', transformedFields)
    },
    enableEdit (index, name) {
      this.editingFieldIndex = index
      this.editingFieldName = name
      this.$nextTick(() => {
      // Auto-focus the input
        const input = this.$el.querySelectorAll('.edit-input')[index]
        if (input) input.focus()
      })
    },
    saveFieldName (index) {
      if (this.editingFieldName.trim()) {
        this.customFields[index].caption = this.editingFieldName.trim()
      }
      this.editingFieldIndex = null
      this.editingFieldName = ''
    },
    openConfigurationListSelector (field, index) {
      this.selected = index
      // Check if field has existing custom_list_id and set selectedList
      if (field.custom_list_id) {
        const existingList = this.customLists.find(list => list.id === field.custom_list_id)
        this.selectedList = existingList || null
      } else {
        this.selectedList = null
      }

      this.openConfig = true
    },
    confirmConfig () {
      if (this.selectedList) {
        const allFields = [...this.fixedFields, ...this.customFields]
        allFields[this.selected].custom_list_id = this.selectedList.id
        allFields[this.selected].custom_list_name = this.selectedList.name
        this.openConfig = false
        this.selectedList = null
        this.selected = null
      } else alert('Choose a custom list')
    },
    cancelConfig () {
      this.selected = null
      this.selectedList = null
      this.openConfig = false
    },
    getFieldTypeId (field) {
      if (field.field_type_id) {
        return field.field_type_id
      }
      const fieldKey = this.getFieldKey(field)
      const match = this.customFormFields.find(f => f.caption === field.caption || f.key === fieldKey)
      return match ? match.id : null
    },
    handleAddCustomField (field) {
      const isDuplicate = this.customFields.some(f => this.getFieldName(f) === field.name)
      if (isDuplicate) {
        alert('Field with same name already exists.')
      } else {
        const newField = {
          autogenerated: false,
          caption: field.name,
          key: field.key,
          name: field.name,
          visibility: false,
          field_id: crypto.randomUUID(),
          use_abbreviation: false,
          required: false
        }

        if (field.key === 'CONFIGURATION_LIST' && field.customList) {
          newField.custom_list_id = field.customList.id
          newField.custom_list_name = field.customList.name
        }

        this.customFields.push(newField)
        this.showFieldModal = false
      }
    },
    deleteCustomField (index) {
      this.customFields.splice(index, 1)
    }
  }
}
</script>

<style lang="scss" scoped>
.sort {
  height: 2em;
  width: 10rem;
  margin: 0 5px 0 5px;
  font-size: var(--text-base);
  background-color: var(--brand-light-color);
  line-height: 1;
  border: 1px solid var(--brand-color);
  border-radius: 4px;
  padding: 4px 12px;
}

.form-canvas {
  display: flex;
  background-color: #fff;
  height: 100%;
}
  .sidebar {
    width: 280px;
    border-right: 1px solid #e0e0e0;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow: hidden;
}
.section {
  padding: 20px 10px 0; // Add padding back to sections

  &:first-child {
    padding-top: 14px; // First section top padding
    flex-shrink: 0;
  }

  &:last-child {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding-bottom: 20px;
  }
}

.section h3 {
  font-size: var(--text-m);
  font-weight: 400;
  color: #4a4a4a;
  margin-bottom: 12px;
}

.field-item {
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 20px;
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 6px;
  transition: background-color 0.3s;
}

.field-item:hover {
  background-color: #f0f0f0;
}

.field-name {
  font-size: var(--text-s);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 120px;
}

.delete-icon {
  width: 12px;
  height: 12px;
  cursor: pointer;
}

.add-field-btn {
  display: flex;
  align-items: center;
  // gap: 8px;
  width: 100%;
  padding: 10px;
  background-color: transparent;
  border: none;
  font-size: var(--text-m);
  font-weight: 500;
  color: #555;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
  flex-shrink: 0; // Prevent shrinking

  &:hover {
    background-color: #f0f0f0;
    color: black;
    font-weight: 400;
  }
}

.preview-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.preview {
  width: 100%;
  max-width: 900px;
  display: flex;
  overflow-y: auto;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}

.preview-scroll-container {
  overflow-y: auto;
  width: 100%;
  max-height: calc(100vh - 180px);
  margin-bottom: 16px;
  padding-right: 6px;
}

.preview-scroll-container::-webkit-scrollbar {
  width: 6px;
}

.preview-scroll-container::-webkit-scrollbar-thumb {
  border-radius: 4px;
}

.template-name {
  font-size: var(--text-3xl);
  margin-bottom: 10px;
  font-weight: 500;
}

.form-type-name {
  font-size: var(--text-s);
  font-weight: 400;
  margin-bottom: 10px;
}

.preview-field {
  border-radius: 10px;
  padding: 15px;
  background-color: #fff;
  min-width: 0;
  display: grid;
  grid-template-columns: 4fr minmax(180px, 1fr);
  column-gap: 20px;
  row-gap: 12px;
}

.field-header{
   display: flex;
   justify-content: space-between;
   align-items: center;
}

.field-options{
  display: flex;
  justify-content: end;
  align-items: center;
}

.field-input {
  display: flex;
  align-items: center;
  gap: 20px;
}

.field-controls {
  display: flex;
  gap: 20px;
}

.config-list-name-pill {
  background-color: #e0f7e5;
  font-size: var(--text-xs2);
  padding: 1px 8px;
  color: #046604;
  border-radius: 8px;
}

.field-index {
  font-size: var(--text-xs2);
  color: var(--brand-color);
  background-color: transparent;
  border: 1px solid var(--brand-light-color);
  border-radius: 6px;
  padding: 2px 6px;
}

.field-title {
  font-size: var(--text-m);
  font-weight: 500;
    white-space: nowrap; // ADDED
  overflow: hidden; // ADDED
  text-overflow: ellipsis; // ADDED
  max-width: 300px; // ADDED
}

.placeholder-input {
  flex: 1;
  min-width: 150px;
  max-width: calc(100vw - 400px);
  padding: 10px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: var(--text-base);
  color: #777;
  white-space: nowrap; // ADDED
  overflow: hidden; // ADDED
  text-overflow: ellipsis; // ADDED
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.toggle-label {
  font-size: var(--text-s);
}

.switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 16px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #aaa;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: '';
  height: 12px;
  width: 12px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #4a4a4a;
}

input:checked + .slider:before {
  transform: translateX(12px);
}

.save-btn {
  margin-top: 20px;
  padding: 10px 20px;
  font-weight: 600;
  background-color: #616161;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.field-icon {
  width: 10px;
  height: 10px;
}

// Fixed fields container with scroll
.fixed-fields-container {
  max-height: 35vh;
  overflow-y: auto;
  padding-right: 4px;
}

.fixed-fields-container::-webkit-scrollbar {
  width: 4px;
}

.fixed-fields-container::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 4px;
}

.fixed-fields-container::-webkit-scrollbar-track {
  background-color: #f5f5f5;
  border-radius: 4px;
}

// Custom fields container with scroll
.custom-fields-container {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 20vh;
  margin-bottom: 5px;
  padding-right: 4px;
}

.custom-fields-container::-webkit-scrollbar {
  width: 4px;
}

.custom-fields-container::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 4px;
}

.custom-fields-container::-webkit-scrollbar-track {
  background-color: #f5f5f5;
  border-radius: 4px;
}

// Fixed field item styles for drag and drop
.fixed-field-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-radius: 6px;
  gap: 10px;
  transition: all 0.2s ease;
  user-select: none;
  margin-bottom: 6px;

  &:hover {
    background-color: #f8f9fa;
  }

  &.dragging {
    opacity: 0.5;
    transform: rotate(2deg);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
}

// Custom field item styles for drag and drop
.custom-field-item {
  min-width: 0;
  max-width: 200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-radius: 6px;
  gap: 10px;
  transition: all 0.2s ease;
  user-select: none;
  margin-bottom: 6px;

  &:hover {
    background-color: #f8f9fa;
  }

  &.dragging {
    opacity: 0.5;
    transform: rotate(2deg);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
}

.drag-handle {
  display: flex;
  align-items: center;
  padding: 2px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.drag-icon {
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.fixed-field-item:hover .drag-icon,
.custom-field-item:hover .drag-icon {
  opacity: 0.8;
}

.field-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
    min-width: 0; // ADDED
  overflow: hidden;
}
.form-info {
  margin: 8px 0 16px 0;
  font-size: var(--text-s);
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.info-item {
  white-space: nowrap;
}

.info-separator {
  color: #ccc;
  font-weight: bold;
}
.line {
  height: 1px;
  background-color: #ccc;
  margin: 12px -10px; // Remove negative margins
}

.edit-input {
  font-size: var(--text-base);
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  max-width: 120px; // ADDED
  min-width: 0;
}

.button-icon {
  width: 16px;
  height: 16px;
  margin-right: 0px;
  margin-bottom: 3px;
  vertical-align: middle;
}

.required-asterisk {
  color: red;
  margin-left: 0px;
}

.dropdown-container {
  padding: 4px 0;
}

.field-group {
  margin-bottom: 24px;
}

.field-group label {
  display: block;
  font-size: var(--text-base);
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.custom-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: var(--text-base);
  background-color: #f9fafb;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  box-sizing: border-box;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
}

.custom-select:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: white;
}

.actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.btn-2 {
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: var(--text-base);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-2-cancel {
  background-color: #f8f9fa;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.btn-2-cancel:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.btn-2-confirm {
  background-color: #9ca3af;
  color: white;
}

.btn-2-confirm-active {
  background-color: #1f2937;
}

.btn-2-confirm-active:hover {
  background-color: #111827;
}

.btn-2:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.dropdown {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.dropdown label {
  margin-top: 5px;
  font-weight: 400;
  font-size: var(--text-base);
}

.config-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.options-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.options-button:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.settings-icon {
  width: 16px;
  height: 16px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.options-button:hover .settings-icon {
  opacity: 1;
}

.options-label {
  font-size: var(--text-s);
  font-weight: 400;
  color: #495057;
  transition: color 0.2s ease;
}

.options-button:hover .options-label {
  color: #343a40;
}
.no-fields-message {
  text-align: center;
  padding: 20px 10px;
  font-size: var(--text-base);
  font-style: italic;
  border-radius: 6px;
}
/* Disabled toggle styles */
// .toggle-wrapper.disabled {
//   opacity: 0.5;
//   pointer-events: none;
// }

.switch.disabled input {
 cursor: not-allowed;
 pointer-events: none;
}

.field-inner {
  flex: 1;
  min-width: 150px;
  max-width: calc(100vw - 400px);
}
.switch.disabled .slider {
 background-color: #838588 !important;
}

.switch.disabled .slider:before {
 background-color: #d1d5db !important;
}

.switch.disabled input:checked + .slider {
 background-color: #e5e7eb !important;
}

.switch.disabled input:checked + .slider:before {
 background-color: #d1d5db !important;
}

.abbreviation-toggle {
  display: inline-flex;
  align-items: center;
  gap: 3px;

  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: #4a4a4a;
  }

  label {
    font-size: var(--text-s);
    color: #495057;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }
}
</style>
